import { Component, Input, OnChanges, Output, EventEmitter, ChangeDetectorRef, inject } from '@angular/core';
import { Chart, ChartType, ChartDataset } from 'chart.js';
import { NWMBarChartSettings } from './NWMChartSettings';
import { APIService } from 'app/services/api.service';
import { AuthenticationService } from 'app/services/auth.service';
import { LoaderService } from "app/services/loader.service";
import { tooltipType } from "app/components/NWMCharts/NWMCharts.module";
import { AppConfig } from "app/app.config";
import { BaseChartDirective } from 'ng2-charts';

type apiDataType = { Percentage: number, Value: string, Year: number }[];

@Component({
  selector: 'CYRChart',
  templateUrl: './nwmchart.component.html',
  styleUrls: ['./nwmchart.component.scss'],
  imports: [BaseChartDirective]
})

export class CYRChartComponent implements OnChanges {
  private api = inject(APIService);
  private auth = inject(AuthenticationService);
  private loaderService = inject(LoaderService);
  private config = inject(AppConfig);
  private ref = inject(ChangeDetectorRef);

  @Input() plan?: string;
  @Input() limit?: number;
  @Output() loaded: EventEmitter<boolean> = new EventEmitter();

  public dataLoaded = false;
  public chartData: ChartDataset[] = [{ data: [], backgroundColor: '#88A8A3' }];
  public chartLabels: string[] = [];
  public chartType: ChartType = 'bar';
  public chartLegend = false;
  public tooltip: tooltipType = { visible: false, rows: [] };
  public chartSettings: NWMBarChartSettings;
  public width = 500;
  public height = 250;
  public valuationDate = '';

  constructor() {
    this.chartSettings = new NWMBarChartSettings();
  }

  ngOnChanges(): void {
    this.dataLoaded = false;
    this.tooltip.visible = false;
    this.chartSettings.options.plugins = { tooltip: { enabled: false, external: (a) => this.customTooltip(a.chart) } };
    this.loadChart();
  }

  customTooltip(chart: Chart): void {
    const tooltipModel = chart.tooltip!;
    this.tooltip.visible = tooltipModel.opacity !== 0;
    if (tooltipModel.body) {
      this.tooltip.rows = [
        { key: 'Year:', value: tooltipModel.title[0] },
        { key: 'Return: ', value: parseFloat(tooltipModel.body[0].lines[0]).toFixed(1) + '%' }
      ];
    }
    setTimeout(() => { this.ref.detectChanges(); });
  }

  loadChart() {
    this.loaderService.display(true, 'CYRChart');
    const reRg = this.auth.getCurrentReRg()!;
    this.valuationDate = this.auth.getCurrentValuationDate();
    const planPath = this.api.getPlanPath(this.plan || '', reRg.IsPershing);

    this.api.get<apiDataType>(`${this.config.portfolio.portfolioSummaryCalendarYearReturnsUrl}/${reRg.ReRgId}/${this.valuationDate}/${planPath}`).subscribe(
      data => {
        this.loaded.emit(false);
        this.loaderService.display(false, 'CYRChart');
        this.dataLoaded = false;
        setTimeout(() => { this.dataLoaded = true });
        this.chartData[0].data = [];
        if (!data || !data.length) return;
        if (this.limit) data = data.slice(-this.limit);
        this.chartLabels = [];
        const chartData: number[] = [];
        const valuationYear = this.valuationDate ? this.valuationDate.toNWMdate().getFullYear() : '';

        for (let i = 0; i < data.length; i++) {
          const yearLabel = data[i].Year.toString();
          if (yearLabel === valuationYear.toString()) { this.chartLabels.push(yearLabel + ' YTD'); }
          else { this.chartLabels.push(yearLabel); }
          chartData.push(+data[i].Value);
        }

        this.chartData[0].data = chartData;

        this.chartSettings.options.scales!['y']!.suggestedMax = this.chartSettings.getSuggestedMax(this.chartData[0].data as number[], .2);
        this.loaded.emit(true);
      },
      () => {
        this.loaded.emit(false);
        this.loaderService.display(false, 'CYRChart');
      }
    );
  }
}
